/* Хвост «Сменить голос в записи» / «Почистить запись» на общем OrderTail (APP-SPEC-5 §6 «Хвост», §16; 03-api.md §9.4, §12.4).
   Этапы, ошибки и деньги — из заказа сервера (OrderTail). Своё: герой — шар голоса (у «Почистить» — микрофон) в кольце, на итоге кольцо
   показывает ход прослушивания; плеер (голос, кнопка, волна из peaks результата или посчитанная из скачанного файла, время);
   второй способ отправки и «Скачать»; «Что дальше»; главное — отправка по входу (deliver: on_request — бот сам не присылает). */
import { useEffect, useRef, useState } from 'react';
import type { Voice } from '../../api/types';
import { voiceOrb } from '../../bridge/voice-orb';
import { uiText } from '../../i18n';
import { platform } from '../../platform';
import { StudioIcon } from '../studio/StudioIcon';
import { OrderTail, type TailAction } from '../studio/circle/OrderTail';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { ToolIcon } from '../studio/circle/ToolIcon';
import { leaveTail, markSeen } from '../studio/circle/tool-helpers';
import { tailPhase } from '../studio/circle/order-tail-model';
import type { VoiceResultUse } from './voice-result-entry';
import { useVoiceDelivery } from './use-voice-delivery';
import { transferVoiceResult } from './voice-transfer';
import { Wave } from './VoiceParts';
import { filePeaks, mmss, otherOut, sendLabel, sentLabel, voiceResult, voiceTexts, type VoiceMode, type VoiceOut } from './voice-model';

export interface VoiceTailProps {
  state: CircleFormState;
  mode: VoiceMode;
  denoise: boolean;
  /** Главный способ отправки по входу */
  out: VoiceOut;
  voice: Voice | null;
  onRetry(): void;
  /** «Заменить запись» (отказ проверки): к форме без записи */
  onReplace(): void;
}

/** Звук итога: авторизованный download (state.openResult) один раз на заказ; волна — из peaks результата или из файла (WebAudio) */
function useResultAudio(state: CircleFormState) {
  const { order, result } = state;
  const asset = order?.result?.available ? order.result.assets.find(item => item.mime.startsWith('audio/')) : undefined;
  const opened = useRef<string>('');
  const server = voiceResult(order);
  const [local, setLocal] = useState<{ url: string; peaks: number[]; durationMs: number } | null>(null);
  const key = order && asset ? `${order.id}:${order.result_revision}:${asset.index}` : '';
  useEffect(() => {
    if (!key || state.result || state.busy || opened.current === key) return;
    opened.current = key;
    void state.openResult(asset!.index);
  });
  const url = result?.mime.startsWith('audio/') ? result.url : '';
  useEffect(() => {
    if (!url || server.peaks) return;
    const request = new AbortController();
    void fetch(url)
      .then(response => response.blob())
      .then(blob => filePeaks(blob, undefined, request.signal))
      .then(value => {
        if (value && !request.signal.aborted) setLocal({ url, ...value });
      })
      .catch(() => {});
    return () => request.abort();
  }, [url, server.peaks]);
  const mine = local?.url === url ? local : null;
  return { asset, url, name: result?.name ?? '', peaks: server.peaks ?? mine?.peaks ?? null, durationMs: server.durationMs ?? mine?.durationMs ?? null };
}

export function VoiceTail({ state, mode, denoise, out, voice, onRetry, onReplace }: VoiceTailProps) {
  const { order, orderId } = state;
  const done = order?.status === 'done';
  const audio = useResultAudio(state);
  const delivery = useVoiceDelivery(orderId ?? 0, done && !!order?.result?.available);
  const player = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [metaMs, setMetaMs] = useState<number | null>(null);
  const [tip, setTip] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');
  const wantDownload = useRef(false);
  useEffect(() => {
    if (!wantDownload.current || !state.result) return;
    wantDownload.current = false;
    const anchor = document.createElement('a');
    anchor.href = state.result.url;
    anchor.download = state.result.name;
    anchor.rel = 'noopener';
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  }, [state.result]);
  if (!orderId) return null;
  const durationMs = audio.durationMs ?? metaMs;
  const progress = durationMs ? Math.min(1, position / durationMs) : 0;
  const texts = voiceTexts(mode, denoise);
  const keep = mode === 'keep';
  const display = voiceResult(order);
  const voiceName = keep ? 'Ваш голос' : display.voiceName || voice?.name || '';
  const orb = !keep && voice ? voiceOrb(voice) : null;
  const picture = keep ? (
    <span className="vc-po no">
      <StudioIcon kind="mic" />
    </span>
  ) : (
    <span className="vc-po" style={orb ? { background: orb } : undefined} />
  );
  const toggle = () => {
    const element = player.current;
    if (!element || !audio.url) return;
    if (element.paused) void element.play().catch(() => setPlaying(false));
    else element.pause();
  };
  const timeText = playing || position > 0 ? mmss(position / 1000) : durationMs ? mmss(durationMs / 1000) : '';
  const heroResult = audio.url ? (
    <div className={`ar-hero vc-hr${playing ? ' on' : position > 0 ? ' paused' : ''}`}>
      <button className="ar-c" type="button" aria-label={uiText(playing ? 'Пауза' : 'Слушать')} onClick={toggle}>
        <span className="ar-pic">
          <i>{picture}</i>
        </span>
        <svg className="ar-ring" viewBox="0 0 100 100" aria-hidden="true">
          <circle className="bg" cx="50" cy="50" r="47.5" />
          <circle className="pl" cx="50" cy="50" r="47.5" pathLength={100} strokeDasharray="100 100" strokeDashoffset={100 - progress * 100} />
        </svg>
        <span className="ar-mid">
          <span className={`pb${playing ? ' on' : ''}`}>
            <ToolIcon kind={playing ? 'pause' : 'play'} />
          </span>
        </span>
        {durationMs && <span className="ar-pill">{playing || position > 0 ? `${mmss(position / 1000)} / ${mmss(durationMs / 1000)}` : mmss(durationMs / 1000)}</span>}
      </button>
    </div>
  ) : undefined;
  const resultView = done ? (
    <>
      {audio.url && (
        <audio
          ref={player}
          src={audio.url}
          preload="metadata"
          hidden
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setPosition(0);
          }}
          onTimeUpdate={event => setPosition(Math.round(event.currentTarget.currentTime * 1000))}
          onLoadedMetadata={event => {
            const ms = Math.round(event.currentTarget.duration * 1000);
            if (Number.isFinite(ms) && ms > 0) setMetaMs(ms);
          }}
        />
      )}
      <div className="vc-pl">
        <div className="top">
          <span className="vn">
            {keep || !orb ? (
              <i className="orb no">
                <StudioIcon kind="mic" />
              </i>
            ) : (
              <i className="orb" style={{ background: orb }} />
            )}
            <span>{uiText(voiceName)}</span>
          </span>
          <span>{uiText(keep ? (denoise ? 'Без шума' : 'Как в записи') : 'Новый голос')}</span>
        </div>
        <div className="ln">
          <button className="pb" type="button" id="vcRpb" aria-label={uiText(playing ? 'Пауза' : 'Слушать')} disabled={!audio.url} onClick={toggle}>
            <ToolIcon kind={playing ? 'pause' : 'play'} />
          </button>
          <div className="wv" id="vcRw" style={{ ['--p' as string]: (progress * 100).toFixed(2) }}>
            {audio.peaks && <Wave peaks={audio.peaks} />}
          </div>
          <span className="tm mono" id="vcRt">
            {timeText}
          </span>
        </div>
      </div>
      {delivery.error && <p role="alert">{uiText(delivery.error)}</p>}
      {moveError && <p role="alert">{uiText(moveError)}</p>}
      {tip && (
        <p className="hintp order-tail-tip" role="status">
          {uiText(tip)}
        </p>
      )}
    </>
  ) : undefined;
  const alt = otherOut(out);
  const ready = !!delivery.detail;
  const sendAction = (kind: VoiceOut, main: boolean): TailAction => {
    const sent = !!delivery.sent[kind];
    const label = sent ? (main ? 'Открыть в чате' : sentLabel(kind)) : sendLabel(kind);
    if (!sent && (!ready || !delivery.can(kind)))
      return { label, icon: kind === 'voice' ? 'mic' : 'doc', onClick() {}, miss: ready ? 'Этот способ отправки сейчас недоступен' : 'Проверяем отправку' };
    return {
      label,
      icon: sent ? (main ? 'send' : 'check') : main ? 'send' : kind === 'voice' ? 'mic' : 'doc',
      disabled: delivery.busy !== null,
      async onClick() {
        if (sent) {
          markSeen(orderId);
          if (main && platform.kind === 'telegram') platform.close();
          else setTip(kind === 'voice' ? 'Голосовое уже в чате с STEOS, в отдельной теме.' : 'Файл уже в чате с STEOS, в отдельной теме.');
          return;
        }
        setTip('');
        await delivery.send(kind);
      },
    };
  };
  const download = () => {
    if (!audio.asset) return;
    wantDownload.current = true;
    if (state.result) {
      const anchor = document.createElement('a');
      anchor.href = state.result.url;
      anchor.download = state.result.name;
      anchor.rel = 'noopener';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      wantDownload.current = false;
      return;
    }
    void state.openResult(audio.asset.index);
  };
  const transfer = async (use: VoiceResultUse) => {
    if (moving || !order) return;
    setMoving(true);
    setMoveError('');
    try {
      await transferVoiceResult({ ownerId: state.ownerId, order, use });
    } catch {
      setMoveError('Не удалось передать запись. Откройте работу в «Истории» и попробуйте снова.');
    } finally {
      setMoving(false);
    }
  };
  const resultOff = !audio.asset || moving;
  return (
    <OrderTail
      state={state}
      kind="voice"
      className="vc-tail"
      hero={{ shape: 'circle', picture, result: tailPhase(order) === 'done' ? heroResult : undefined }}
      texts={texts}
      result={resultView}
      acts={[
        sendAction(alt, false),
        { label: 'Скачать', icon: 'download', disabled: !audio.asset, onClick: download },
      ]}
      next={[
        { label: 'Изменить', icon: 'edit', onClick: () => void leaveTail(state) },
        { label: 'Сделать кружок', icon: 'send', disabled: resultOff, onClick: () => transfer('audio') },
        { label: 'Заставить говорить', icon: 'user', disabled: resultOff, onClick: () => transfer('talk') },
      ]}
      foot={{ done: sendAction(out, true), mod: { label: 'Заменить запись', icon: 'upload', onClick: onReplace } }}
      modAction="Заменить запись"
      backLabel="К настройкам"
      onRetry={onRetry}
    />
  );
}
